import { useCallback, useEffect, useRef, useState } from 'react';

interface UseVideoMouseRestOptions {
  delay?: number;
  enabled?: boolean;
  isPlaying: boolean;
}

interface MouseRestHandlers {
  onControlsBlur: (e: React.FocusEvent) => void;
  onControlsFocus: (e: React.FocusEvent) => void;
  onControlsMouseEnter: (e: React.MouseEvent) => void;
  onControlsMouseLeave: (e: React.MouseEvent) => void;
  onMouseEnter: (e: React.MouseEvent) => void;
  onMouseLeave: (e: React.MouseEvent) => void;
  onMouseMove: (e: React.MouseEvent) => void;
}

export function useVideoMouseRest({
  delay = 2000,
  enabled = true,
  isPlaying,
}: UseVideoMouseRestOptions): {
  shouldShowUI: boolean;
  handlers: MouseRestHandlers;
} {
  const [shouldShowUI, setShouldShowUI] = useState(true);
  const timerRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const isMouseOverRef = useRef(false);
  const isOverControlsRef = useRef(false);
  const isControlsFocusedRef = useRef(false);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = undefined;
    }
  }, []);

  const showUI = useCallback(() => {
    setShouldShowUI(true);
  }, []);

  const hideUI = useCallback(() => {
    if (
      isPlaying &&
      !isOverControlsRef.current &&
      !isControlsFocusedRef.current
    ) {
      setShouldShowUI(false);
    }
  }, [isPlaying]);

  const startTimer = useCallback(() => {
    clearTimer();

    if (
      enabled &&
      isPlaying &&
      !isOverControlsRef.current &&
      !isControlsFocusedRef.current
    ) {
      timerRef.current = setTimeout(hideUI, delay);
    }
  }, [clearTimer, delay, enabled, isPlaying, hideUI]);

  const handleMouseMove = useCallback(() => {
    showUI();
    startTimer();
  }, [showUI, startTimer]);

  const handleMouseEnter = useCallback(() => {
    isMouseOverRef.current = true;
    showUI();
    startTimer();
  }, [showUI, startTimer]);

  const handleMouseLeave = useCallback(() => {
    isMouseOverRef.current = false;
    hideUI();
    clearTimer();
  }, [hideUI, clearTimer]);

  const handleControlsMouseEnter = useCallback(() => {
    isOverControlsRef.current = true;
    showUI();
    clearTimer();
  }, [showUI, clearTimer]);

  const handleControlsMouseLeave = useCallback(() => {
    isOverControlsRef.current = false;

    if (!isControlsFocusedRef.current) {
      startTimer();
    }
  }, [startTimer]);

  const handleControlsFocus = useCallback(
    (e: React.FocusEvent) => {
      try {
        if (e.target.matches(':focus-visible')) {
          isControlsFocusedRef.current = true;
          showUI();
          clearTimer();
        }
      } catch {
        // Gracefully fail for browsers that don't support :focus-visible
      }
    },
    [showUI, clearTimer]
  );

  const handleControlsBlur = useCallback(() => {
    if (isControlsFocusedRef.current) {
      isControlsFocusedRef.current = false;

      if (!isOverControlsRef.current) {
        hideUI();
        clearTimer();
      }
    }
  }, [hideUI, clearTimer]);

  const handlePlayingStateChange = useCallback(() => {
    if (!isPlaying) {
      showUI();
      clearTimer();
    } else if (isMouseOverRef.current) {
      startTimer();
    } else {
      hideUI();
    }
  }, [isPlaying, showUI, hideUI, clearTimer, startTimer]);

  useEffect(() => {
    handlePlayingStateChange();
  }, [handlePlayingStateChange]);

  useEffect(
    () => () => {
      clearTimer();
    },
    [clearTimer]
  );

  return {
    shouldShowUI,
    handlers: {
      onMouseMove: handleMouseMove,
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
      onControlsMouseEnter: handleControlsMouseEnter,
      onControlsMouseLeave: handleControlsMouseLeave,
      onControlsFocus: handleControlsFocus,
      onControlsBlur: handleControlsBlur,
    },
  };
}
